import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, RefreshCw, AlertTriangle, CheckCircle2, Download, ThumbsUp, ThumbsDown, Circle } from "lucide-react";

/**
 * Phase R6 — Decision Summary AI dialog.
 *
 * Reads every interview round + Q&A + notes + votes + analysis and asks
 * Claude for a structured hire/no-hire memo. Draft is persisted so subsequent
 * opens load instantly from cache; a "Redraft" button forces a fresh AI call.
 */
const RECOMMENDATION_UI = {
    strong_hire: { label: "Strong hire", icon: CheckCircle2, tint: "border-emerald-500 bg-emerald-50 text-emerald-700" },
    hire:        { label: "Hire",         icon: ThumbsUp,      tint: "border-emerald-400 bg-emerald-50 text-emerald-700" },
    hold:        { label: "Hold",         icon: Circle,        tint: "border-amber-400 bg-amber-50 text-amber-800" },
    no_hire:     { label: "No hire",      icon: ThumbsDown,    tint: "border-rose-400 bg-rose-50 text-rose-700" },
};

export default function DecisionMemoDialog({ open, onClose, candidateId, jobId, candidateName, jobTitle }) {
    const [memo, setMemo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);

    const loadCached = useCallback(async () => {
        if (!candidateId || !jobId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/interviews/decision-summary/${candidateId}/${jobId}`);
            setMemo(data?.memo || null);
        } catch (err) {
            // 404-ish is fine — no memo yet
            setMemo(null);
        } finally { setLoading(false); }
    }, [candidateId, jobId]);

    useEffect(() => { if (open) loadCached(); }, [open, loadCached]);

    const generate = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post("/interviews/decision-summary", { candidate_id: candidateId, job_id: jobId });
            setMemo(data);
            toast.success("Decision memo drafted");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setGenerating(false); }
    };

    const download = () => {
        if (!memo) return;
        const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Decision Memo · ${candidateName || ""}</title>
<style>
body{font-family:-apple-system,'Segoe UI',sans-serif;color:#0f172a;padding:40px;max-width:820px;margin:auto;line-height:1.55}
h1{font-size:22px;margin:0 0 4px}
h2{font-size:14px;text-transform:uppercase;letter-spacing:.1em;color:#64748b;margin:24px 0 6px;border-bottom:1px solid #e2e8f0;padding-bottom:4px}
.badge{display:inline-block;padding:4px 10px;border:1px solid #cbd5e1;font-size:11px;letter-spacing:.08em;text-transform:uppercase;font-family:monospace}
.hire{background:#ecfdf5;color:#047857;border-color:#6ee7b7}
.hold{background:#fefce8;color:#92400e;border-color:#fde047}
.nohire{background:#fef2f2;color:#b91c1c;border-color:#fca5a5}
ul{padding-left:20px}
.kpi{display:inline-block;padding:6px 12px;border:1px solid #e2e8f0;margin-right:8px;font-family:monospace;font-size:12px}
@media print{body{padding:20px}}
</style></head><body>
<h1>${candidateName || "—"} · ${jobTitle || ""}</h1>
<div style="margin-top:6px"><span class="badge ${memo.recommendation==='no_hire'?'nohire':memo.recommendation==='hold'?'hold':'hire'}">${(memo.recommendation||'').replace('_',' ')}</span>
<span class="badge">Confidence: ${memo.confidence}</span></div>
<h2>Headline</h2><p>${memo.headline || ""}</p>
<h2>Summary</h2>${memo.summary_html || ""}
${memo.score_breakdown ? `<h2>Score breakdown</h2>${Object.entries(memo.score_breakdown).map(([k,v])=>`<span class="kpi">${k.replace(/_/g,' ')}: ${v}</span>`).join('')}` : ""}
${memo.strengths?.length ? `<h2>Strengths</h2><ul>${memo.strengths.map(s=>`<li>${s}</li>`).join('')}</ul>` : ""}
${memo.concerns?.length ? `<h2>Concerns</h2><ul>${memo.concerns.map(s=>`<li>${s}</li>`).join('')}</ul>` : ""}
${memo.risks?.length ? `<h2>Risks</h2><ul>${memo.risks.map(s=>`<li>${s}</li>`).join('')}</ul>` : ""}
${memo.next_steps?.length ? `<h2>Next steps</h2><ol>${memo.next_steps.map(s=>`<li>${s}</li>`).join('')}</ol>` : ""}
<p style="margin-top:32px;font-size:11px;color:#94a3b8">Drafted by ${memo.generated_by_name || "AI"} · ${(memo.created_at||"").slice(0,16).replace('T',' ')}</p>
</body></html>`;
        const w = window.open("", "_blank");
        w.document.write(html);
        w.document.close();
        setTimeout(() => w.print(), 400);
    };

    const rec = memo?.recommendation && RECOMMENDATION_UI[memo.recommendation];
    const RecIcon = rec?.icon;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="decision-memo-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-xl font-black">
                        <Sparkles className="w-5 h-5 text-ikb" /> Decision memo
                    </DialogTitle>
                    <DialogDescription>
                        AI-drafted hire recommendation for <span className="font-medium">{candidateName || "candidate"}</span>
                        {jobTitle ? <> · <span className="font-medium">{jobTitle}</span></> : null}
                    </DialogDescription>
                </DialogHeader>

                {loading && <div className="py-8 text-sm text-muted-foreground text-center">Loading previous memo…</div>}

                {!loading && !memo && (
                    <div className="border border-dashed border-border p-6 text-center space-y-3" data-testid="dm-empty">
                        <div className="text-sm text-foreground/70">No memo yet. Claude will read every round, note, rating, vote, and Q&amp;A on file to draft a structured hire/no-hire recommendation.</div>
                        <Button onClick={generate} disabled={generating} className="rounded-none bg-ikb hover:bg-ikb/90" data-testid="dm-draft-btn">
                            {generating ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                            {generating ? "Drafting memo…" : "Draft with AI"}
                        </Button>
                    </div>
                )}

                {!loading && memo && (
                    <div className="space-y-4" data-testid="dm-memo">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2">
                                <span className={`px-3 py-1 border font-mono uppercase tracking-widest text-[11px] inline-flex items-center gap-1.5 ${rec?.tint || "border-border"}`} data-testid="dm-recommendation">
                                    {RecIcon && <RecIcon className="w-3.5 h-3.5" />} {rec?.label || memo.recommendation}
                                </span>
                                <span className="text-[10px] font-mono uppercase text-muted-foreground border border-border px-2 py-0.5">Confidence: {memo.confidence}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Button variant="outline" onClick={generate} disabled={generating} className="rounded-none h-8 text-xs" data-testid="dm-redraft-btn">
                                    {generating ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} Redraft
                                </Button>
                                <Button variant="outline" onClick={download} className="rounded-none h-8 text-xs" data-testid="dm-download-btn">
                                    <Download className="w-3 h-3 mr-1" /> Export PDF
                                </Button>
                            </div>
                        </div>

                        {memo.headline && <div className="text-base font-semibold leading-snug" data-testid="dm-headline">{memo.headline}</div>}
                        {memo.summary_html && <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: memo.summary_html }} data-testid="dm-summary" />}

                        {memo.score_breakdown && Object.keys(memo.score_breakdown).length > 0 && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="dm-scores">
                                {Object.entries(memo.score_breakdown).map(([k, v]) => (
                                    <div key={k} className="border border-border bg-white p-2">
                                        <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{k.replace(/_/g, " ")}</div>
                                        <div className="text-lg font-black font-mono text-ikb">{v}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <MemoList title="Strengths" items={memo.strengths} tone="emerald" testid="dm-strengths" />
                        <MemoList title="Concerns" items={memo.concerns} tone="amber" testid="dm-concerns" />
                        <MemoList title="Risks" items={memo.risks} tone="rose" testid="dm-risks" icon={AlertTriangle} />
                        <MemoList title="Next steps" items={memo.next_steps} tone="ikb" numbered testid="dm-nextsteps" />

                        <div className="text-[10px] text-muted-foreground pt-2 border-t border-border">
                            Drafted {(memo.created_at || "").slice(0, 16).replace("T", " ")} · {memo.generated_by_name || "AI"}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function MemoList({ title, items, tone = "muted", numbered = false, icon: Icon = null, testid }) {
    if (!items || items.length === 0) return null;
    const tones = {
        emerald: "border-l-emerald-500 bg-emerald-50/40",
        amber:   "border-l-amber-500 bg-amber-50/40",
        rose:    "border-l-rose-500 bg-rose-50/40",
        ikb:     "border-l-ikb bg-ikb/5",
        muted:   "border-l-border bg-secondary/30",
    };
    return (
        <section className={`border-l-4 pl-3 py-2 ${tones[tone] || tones.muted}`} data-testid={testid}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5">
                {Icon && <Icon className="w-3 h-3" />} {title}
            </div>
            <ul className={`text-sm space-y-1 ${numbered ? "list-decimal pl-5" : "list-disc pl-5"}`}>
                {items.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
        </section>
    );
}
